import { pxToRem } from '@withjoy/joykit/theme';
import { ReactComponent as HelpIcon } from '@assets/icons/help.svg';
import { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { Flex, Box, styled, StyleSystemProps } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { FriendsRegistryBannerVariant } from './AddFromFriendsRegistryBanner';

const DESKTOP_WIDTH = 600;
const DESKTOP_HEIGHT = 680;
const DESKTOP_SMALL_WIDTH = '48vw';
const DESKTOP_SMALL_HEIGHT = 570;
const MOBILE_HEIGHT = 390;
const MOBILE_TEXT_WIDTH = 310;
const DESKTOP_TEXT_HEADING_WIDTH = 464;
const DESKTOP_TEXT_FORM_WIDTH = 419;

export const StyledHelpIcon = styled(HelpIcon)`
  transition: ${animationTransition('color')};
  :hover {
    color: black;
    cursor: pointer;
  }
`;

const variantsStyles: {
  wrapper: {
    default: StyleSystemProps;
    animated: StyleSystemProps;
  };
  textContainer: {
    default: StyleSystemProps;
    animated: StyleSystemProps;
  };
} = {
  wrapper: {
    default: {
      columnGap: pxToRem(56),
      paddingY: {
        _: 6,
        sm2: 9
      },
      paddingX: 6,
      backgroundColor: 'mono1'
    },
    animated: {
      columnGap: 0,
      padding: 0,
      backgroundColor: 'white',
      width: {
        _: '100%',
        sm2: '98%'
      },
      marginX: 'auto',
      borderRadius: {
        _: 0,
        sm2: 4
      },
      overflow: 'hidden'
    }
  },
  textContainer: {
    default: {
      columnGap: pxToRem(56),
      rowGap: 6,
      flexDirection: {
        _: 'column',
        sm2: 'row'
      }
    },
    animated: {
      flexDirection: 'column',
      width: {
        _: '100%',
        sm2: DESKTOP_SMALL_WIDTH,
        md2: pxToRem(DESKTOP_WIDTH)
      },
      height: {
        _: 'auto',
        sm2: pxToRem(DESKTOP_SMALL_HEIGHT),
        md2: pxToRem(DESKTOP_HEIGHT)
      },
      justifyContent: 'center',
      alignItems: {
        _: 'center',
        sm2: 'flex-start'
      },
      paddingLeft: {
        _: 8,
        md: 9,
        md3: pxToRem(69)
      },
      paddingRight: {
        _: 8,
        md: 0
      },
      rowGap: 8,
      border: {
        _: 0,
        sm2: '6px solid'
      },
      borderLeft: {
        _: 0,
        sm2: '0px solid'
      },
      borderColor: {
        _: 'initial',
        sm2: '#FDD1F4'
      },
      borderTopRightRadius: {
        _: 0,
        sm2: 4
      },
      borderBottomRightRadius: {
        _: 0,
        sm2: 4
      }
    }
  }
};

export const StyledWrapper = styled(Flex)``;
const wrapperStyles = (variant: FriendsRegistryBannerVariant): StyleSystemProps => ({
  ...variantsStyles.wrapper[variant],
  rowGap: 6,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: {
    _: 'column',
    sm2: 'row'
  }
});

export const StyledHeading = styled(Box)``;
const headingStyles: StyleSystemProps = {
  width: {
    _: `calc(100vw - ${pxToRem(48)})`,
    sm2: pxToRem(MOBILE_TEXT_WIDTH),
    md: pxToRem(DESKTOP_TEXT_HEADING_WIDTH)
  }
};

export const StyledFormWrapper = styled(Flex)``;
const formWrapperStyles: StyleSystemProps = {
  width: {
    _: `calc(100vw - ${pxToRem(48)})`,
    sm2: pxToRem(MOBILE_TEXT_WIDTH),
    md: pxToRem(DESKTOP_TEXT_FORM_WIDTH)
  },
  flexDirection: 'column'
};

export const StyledMediaContainer = styled(Flex)``;
const mediaContainerStyles: StyleSystemProps = {
  position: 'relative',
  width: {
    _: '100%',
    sm2: DESKTOP_SMALL_WIDTH,
    md2: pxToRem(DESKTOP_WIDTH)
  },
  height: {
    _: pxToRem(MOBILE_HEIGHT),
    sm2: 'auto'
  }
};

const medialLayoutStyles: CSSObject = {
  position: 'absolute',
  width: {
    _: '100%',
    sm2: DESKTOP_SMALL_WIDTH,
    md2: pxToRem(DESKTOP_WIDTH)
  },
  height: {
    _: pxToRem(MOBILE_HEIGHT),
    sm2: pxToRem(DESKTOP_HEIGHT)
  },
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)'
};

export const StyledBackgroundVideo = styled(Box)``;
const backgroundVideoStyles = ($backgroundImageUrl: Maybe<string>): CSSObject => ({
  ...medialLayoutStyles,
  backgroundImage: $backgroundImageUrl ? `url("${$backgroundImageUrl}")` : undefined,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  overflow: 'hidden',
  zIndex: 2,
  borderTopLeftRadius: {
    _: 0,
    sm2: 4
  },
  borderBottomLeftRadius: {
    _: 0,
    sm2: 4
  },
  ['& iframe']: {
    ...medialLayoutStyles,
    width: {
      _: '100%',
      sm2: pxToRem(DESKTOP_WIDTH)
    },
    height: {
      _: '100vw',
      sm2: '100%'
    }
  }
});

export const StyledTextContainer = styled(Flex)``;
const textContainerStyles = (variant: FriendsRegistryBannerVariant): StyleSystemProps => ({
  ...variantsStyles.textContainer[variant]
});

export const styles = {
  wrapper: wrapperStyles,
  heading: headingStyles,
  formWrapper: formWrapperStyles,
  mediaContainer: mediaContainerStyles,
  backgroundVideo: backgroundVideoStyles,
  textContainer: textContainerStyles
};
